import {
  PkAccordionGroup,
  PkAccordionItem,
  PkAccordionItemContent,
  PkAccordionItemTitle,
  PkAvatar,
  PkDivider,
  PkIcon,
  PkIdentifier,
  PkListItem,
} from '@uefadigital/panenka-react';
import i18n from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { PlayerStatus, SquadGroup, SquadPlayer, Stats } from 'types/ApiResponseInterfaces';
import type { ConfigCompetition } from 'utils/config';
import { translateEntity } from 'utils/translateEntity';

interface Team {
  id?: string;
  internationalName?: string;
}

interface SquadDetailsTableData {
  squad: SquadGroup;
  competition?: ConfigCompetition;
  teams: Team[] | undefined;
}

const generatePlayerName = (firstName: string, lastName: string) =>
  `${firstName.length ? `${firstName[0]}.` : ''} ${lastName}`;

const PlayerAvailability = ({ playerStatus }: { playerStatus: PlayerStatus | undefined }) => {
  const { t } = useTranslation();

  if (playerStatus?.isSuspended) {
    return (
      <p slot="primary" className="pk-d--flex pk-align-items--center pk-mb--0">
        <PkIcon name="football-suspended" size={20} color="support-04A" className="pk-mr--xs2" />
        <span className="pk-text--text-02 pk-font-size--xs pr-font-size--10 pk-font-weight--normal pk-line-height--md">
          {t('suspended')}
        </span>
      </p>
    );
  }

  if (playerStatus?.isBooked) {
    return (
      <p slot="primary" className="pk-d--flex  pk-align-items--center pk-mb--0 pk-text--wrap pk-pr--xl">
        <PkIcon name="football-card-single-card" size={21} className="pk-text--support-05 pk-mr--xs2" />
        <span className="pk-text--text-02 pk-font-size--xs pr-font-size--10 pk-font-weight--normal pk-line-height--md">
          {t('misses_next_match_if_booked')}
        </span>
      </p>
    );
  }

  return null;
};

export const SquadDetailsAccordion = ({ squad, competition, teams }: SquadDetailsTableData) => {
  const { t } = useTranslation();

  let indexCurrentSeason = 0;
  let indexAllTime = 0;
  let indexAllUefaComp = 0;

  const isNational = competition?.type === 'national';

  const headingCells = useMemo(() => {
    return [t('name'), t(isNational ? 'club' : 'nationality'), t('date_of_birth'), t('age')];
  }, [isNational, t]);

  const addDetails = (statistic: Array<Stats> | undefined) => {
    statistic?.forEach((stats, idx) => {
      if (idx === 0) {
        indexCurrentSeason = 0;
      } else if (idx === 1) {
        indexAllTime = 1;
      } else {
        indexAllUefaComp = 2;
      }

      headingCells.push(t(stats?.name));
    });
  };

  addDetails(squad.players[0].stats);

  const playerBirthday = (playerDate: string) => {
    const dateNow = new Date()
      .toLocaleDateString(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
      .replace(/ /g, ' ');

    return dateNow === playerDate ? <PkIcon name="features-hospitality" size={20} class="pk-mr--xs" /> : '';
  };

  const displayStatisticData = (statisticArr: Array<Stats> | undefined, headingCells: Array<string>, id: string) => {
    return statisticArr?.map((statistic, i: number) => {
      const isGoalsStats =
        statistic?.name === 'matches_appearance/goals' || statistic?.name === 'matches_appearance/goals_conceded';

      return (
        <PkListItem key={+id + i} hasDivider={statisticArr.length - 1 !== i}>
          <div slot="prefix-card" className="pk-py--l">
            <span className="pk-text--text-01 pk-font-size--m pk-font-weight--normal">{headingCells[i + 4]}</span>
            {isGoalsStats && (i === indexCurrentSeason || i === indexAllTime) && (
              <div className="pk-pt--xs pk-font-size--xs pk-text--ui-06 pr-font-size--10 pr-font-weight--400">
                {competition?.description}
              </div>
            )}
            {isGoalsStats && (
              <div className="pk-pt--xs pk-font-size--xs pk-text--ui-06 pr-font-size--10 pr-font-weight--400">
                {i === indexAllUefaComp
                  ? t(isNational ? 'national_team_all_time' : 'uefa_all_time')
                  : i === indexCurrentSeason
                    ? t('current_season')
                    : t('all_time')}
              </div>
            )}
          </div>
          <div slot="suffix-card" className="pk-text--text-01 pk-font-weight--bold">
            {statistic?.value}
          </div>
        </PkListItem>
      );
    });
  };

  const displayStatistic = (player: SquadPlayer, statisticName: Array<string>, id: string) => {
    const playerDate = new Date(player?.player?.birthDate ?? '')
      .toLocaleDateString(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
      .replace(/ /g, ' ');

    return (
      <PkAccordionItemContent>
        <PkDivider />
        <PkListItem>
          <div slot="prefix-card" className="pk-py--l">
            <span className="pk-text--text-01 pk-font-size--m pk-font-weight--normal">{statisticName[1]}</span>
          </div>
          <div slot="suffix-card" className="pk-text--text-01 pk-font-weight--bold">
            {isNational && teams
              ? teams.find((team) => team.id === player?.player?.clubId)?.internationalName
              : translateEntity(player?.player)?.countryName || t('unknown')}
          </div>
        </PkListItem>
        <PkListItem>
          <div slot="prefix-card" className="pk-py--l">
            <span className="pk-text--text-01 pk-font-size--m pk-font-weight--normal">{statisticName[2]}</span>
          </div>
          <div slot="suffix-card" className="pk-text--text-01 pk-font-weight--bold pk-d--flex pk-justify-content--end">
            {player.player.birthDate} {playerBirthday(playerDate)}
          </div>
        </PkListItem>
        <PkListItem>
          <div slot="prefix-card" className="pk-py--l">
            <span className="pk-text--text-01 pk-font-size--m pk-font-weight--normal">{statisticName[3]}</span>
          </div>
          <div slot="suffix-card" className="pk-text--text-01 pk-font-weight--bold">
            {player.player.age}
          </div>
        </PkListItem>
        {displayStatisticData(player?.stats, statisticName, id)}
      </PkAccordionItemContent>
    );
  };

  return (
    <div className="pk-m--0 pk-p--0 pk-mb--s">
      <div className="pk-text--text-01 pk-mb--s pk-font-size--l pk-line-height--md pk-font--base-regular">
        {translateEntity(squad)?.fieldPosition}
      </div>
      <PkAccordionGroup multipleOpen>
        {squad.players.map((play, i) => {
          const translatePlayer = t('player');

          const firstName = translateEntity(play.player)?.firstName ?? '';
          const lastName = translateEntity(play.player)?.lastName ?? translatePlayer;

          const playerName = generatePlayerName(firstName, lastName);

          return (
            <PkAccordionItem
              key={i}
              hasDivider={false}
              expanded={false}
              className="pk-bg--elevation-01 pk-px--m pk-pb--0"
            >
              <PkAccordionItemTitle
                iconPosition="right"
                circleIcon={false}
                iconColor="text-02"
                textColor="ui-06"
                className="pk-font-size--xl pk-line-height--md pk-py--xs"
              >
                <PkIdentifier
                  alignment="left"
                  className={`${play?.jerseyNumber && play?.jerseyNumber < 10 ? 'pk-pl--xs' : ''}`}
                >
                  <span slot="prefix">
                    <span className="pk-pr--xs pk-text--text-01">{play.jerseyNumber}</span>
                    <PkAvatar
                      type="image"
                      src={play.player.imageUrl}
                      class="hydrated"
                      style={{ borderRadius: '24px' }}
                    />
                  </span>
                  <span slot="primary" className="pk-text--text-01">
                    {playerName}
                  </span>
                  <PlayerAvailability playerStatus={play.status} />
                </PkIdentifier>
              </PkAccordionItemTitle>
              {displayStatistic(play, headingCells, play.player.id)}
            </PkAccordionItem>
          );
        })}
      </PkAccordionGroup>
    </div>
  );
};
